import { useCallback } from "react";
import * as Sentry from "@sentry/react";
import {
  createAccount,
  upsertContact,
  createDeal,
  uploadPDF,
  uploadDocumentToCRM,
  fetchAllLists,
  addContactToList,
} from "../utils/freshsalesApis";
import { createTask, uploadFilesToZoho } from "../utils/zohoApis";
import { surveyGeneratePDF } from "../utils/pdfUtils";
import { defaultSurveyJson } from "../surveyJson";

export function useSurveySubmission(
  setPdfData,
  setIsSubmitted,
  setOnSubmitting,
  setError // Add setError to the parameters
) {
  return useCallback(async (sender) => {
    // Possible states for tasks: "Not Completed", "Completed", "Not Requested"
    const taskProgress = {
      accountCreated: "Not Completed",
      pdfUploaded: "Not Completed",
      contactCreated: "Not Completed",
      dealCreated: "Not Completed",
      addedToList: "Not Completed",
      googlePageTaskCreated: "Not Completed",
      flyerTaskCreated: "Not Completed",
      websiteTaskCreated: "Not Completed",
      menuUploaded: "Not Completed",
    };

    try {
      setOnSubmitting(true);
      const inputData = sender.data;

      const PDFBlob = await surveyGeneratePDF(defaultSurveyJson, sender);
      setPdfData(PDFBlob);

      await processSubmission(inputData, PDFBlob, taskProgress);
      setOnSubmitting(false);
      setIsSubmitted(true);
    } catch (error) {
      setOnSubmitting(false);
      const errorInfo = { message: error.message, taskProgress };
      setError(errorInfo);

      // Send error to Sentry with context
      Sentry.withScope((scope) => {
        scope.setExtra("formData", sender.data);
        scope.setExtra("taskProgress", taskProgress);
        scope.setLevel('error'); // Use string literal instead of Sentry.Severity.Error
        Sentry.captureException(error);
      });

      console.error("Error processing submission:", error);
    }
    // eslint-disable-next-line
  }, []);

  async function processSubmission(inputData, PDFBlob, taskProgress) {
    try {
      // Create CRM Account
      const account = await createAccount(inputData);
      taskProgress.accountCreated = "Completed";

      // Upload PDF to CRM acccount
      await uploadPDF(inputData, account.sales_account.id, PDFBlob);
      taskProgress.pdfUploaded = "Completed";

      // Upsert (Create or Update) CRM Contact
      let contact = await upsertContact(inputData, account.sales_account.id);
      taskProgress.contactCreated = "Completed";

      // Create CRM Deal
      await createDeal(inputData, account.sales_account.id, contact.contact.id);
      taskProgress.dealCreated = "Completed";

      // Add to CRM List
      const lists = await fetchAllLists();
      const newClientsList = lists.find((list) => list.name === "New Clients");
      if (newClientsList) {
        await addContactToList(newClientsList.id, contact.contact.id);
        taskProgress.addedToList = "Completed";
      }
      
      // Create Google Page Task on Zoho Connect
      if (inputData?.requests?.includes("googlePage")) {
        await createTask("googlePage", inputData.businessName);
        taskProgress.googlePageTaskCreated = "Completed";
      }
      taskProgress.googlePageTaskCreated = "Not Requested";

      // Create Flyer Task on Zoho Connect
      if (inputData?.requests?.includes("flyer")) {
        await createTask("flyer", inputData.businessName);
        taskProgress.flyerTaskCreated = "Completed";
      }
      taskProgress.flyerTaskCreated = "Not Requested";

      // Create Website Task on Zoho Connect
      if (inputData?.requests?.includes("website")) {
        let uploadedFileIds = [];
        if (inputData.menu && inputData.menu.length > 0) {
          for (const file of inputData.menu) {
            const uploadedFileId = await uploadFilesToZoho(file);
            uploadedFileIds.push(uploadedFileId);
          }
        }
        await createTask("website", inputData.businessName, uploadedFileIds);
        taskProgress.websiteTaskCreated = "Completed";
      }
      taskProgress.websiteTaskCreated = "Not Requested";

      // Upload Documents (Menu) to CRM acccount
      if (inputData.menu && inputData.menu.length > 0) {
        for (const file of inputData.menu) {
          await uploadDocumentToCRM(file, account.sales_account.id);
          taskProgress.menuUploaded = "Completed";
        }
      }
    } catch (error) {
      // Enhance error with more context to Send to Sentry
      const enhancedError = new Error(
        `Error in ${getFailedTaskMessage(taskProgress)}`
      );
      enhancedError.originalError = error;
      enhancedError.context = { taskProgress, inputData };
      throw enhancedError;
    }
  }

  // Helper function to determine where the process failed
  function getFailedTaskMessage(taskProgress) {
    if (taskProgress.accountCreated === "Not Completed") return "Account Creation";
    if (taskProgress.pdfUploaded === "Not Completed") return "Pdf Upload";
    if (taskProgress.contactCreated === "Not Completed") return "Contact Creation";
    if (taskProgress.dealCreated === "Not Completed") return "Deal Creation";
    if (taskProgress.addedToList === "Not Completed") return "Adding to List";
    if (taskProgress.googlePageTaskCreated === "Not Completed") return "Google Page Task Creation";
    if (taskProgress.flyerTaskCreated === "Not Completed") return "Flyer Task Creation";
    if (taskProgress.websiteTaskCreated === "Not Completed") return "Website Task Creation";
    if (taskProgress.menuUploaded === "Not Completed") return "Menu Upload";
    return "Unknown Task";
  }
}
